import { useState } from 'react';
import { jobSharedAttachmentsApi } from '../lib/jobSharedAttachments';
import { jobQualityApi,type QualityCheckpoint } from '../lib/jobQuality';
import { useRpcResource } from '../lib/useRpcResource';
import { Card,ErrorPanel,Loading } from './ResourceView';

const money=(value:number)=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(value);
const label=(value:string)=>value.replaceAll('_',' ');
const checkpointBadge=(value:QualityCheckpoint)=>value==='post_install'?'After-install evidence':value==='pre_teardown'?'Pre-teardown evidence':'Return inspection evidence';

export function JobQualityCheck({jobId,checkpoint,onChanged}:{jobId:string;checkpoint:QualityCheckpoint;onChanged?:()=>void|Promise<void>}){
  const r=useRpcResource(()=>jobQualityApi.workspace(jobId,checkpoint),[jobId,checkpoint]);
  const[file,setFile]=useState<File|null>(null);
  const[damaged,setDamaged]=useState(false);
  const[productId,setProductId]=useState('');
  const[quantity,setQuantity]=useState(1);
  const[notes,setNotes]=useState('');
  const[busy,setBusy]=useState(false);
  const[error,setError]=useState<string|null>(null);
  const[notice,setNotice]=useState<string|null>(null);
  async function refreshAt(top:number){
    await r.reload();await onChanged?.();
    requestAnimationFrame(()=>requestAnimationFrame(()=>window.scrollTo({top,behavior:'auto'})));
  }
  async function upload(){
    if(!file||!r.data?.capabilities.update)return;
    const top=window.scrollY;setBusy(true);setError(null);setNotice(null);
    const x=await jobSharedAttachmentsApi.uploadQualityPhoto(r.data.organization_id,jobId,checkpoint,file);
    if(x.error)setError(x.error.message);
    else{setFile(null);setNotice('Quality Control photo added to the shared Job attachments.');await refreshAt(top)}
    setBusy(false);
  }
  async function complete(){
    if(!r.data?.capabilities.update)return;
    const top=window.scrollY;setBusy(true);setError(null);setNotice(null);
    const x=await jobQualityApi.complete(jobId,checkpoint,damaged,notes.trim()||null,damaged?productId:null,quantity);
    if(x.error)setError(x.error.message);
    else{
      const generated=[
        x.data?.maintenance_work_order_number?`Maintenance ${x.data.maintenance_work_order_number}`:null,
        x.data?.damage_claim_number?`Finance claim ${x.data.damage_claim_number}`:null,
      ].filter(Boolean).join(' · ');
      setNotice(damaged?`Damage recorded as ${label(x.data?.damage_attribution??'pending')}.${generated?` ${generated} created.`:''}`:'Quality Control marked clear.');
      await refreshAt(top);
    }
    setBusy(false);
  }
  if(r.loading&&!r.data)return <Card title="Quality Control"><Loading/></Card>;
  if(r.error)return <Card title="Quality Control"><ErrorPanel message={r.error}/></Card>;
  if(!r.data)return null;
  const d=r.data,check=d.check,selected=d.damage_parts.find(p=>p.product_id===productId);
  const completed=check.status==='completed';
  return <Card title={d.title}>
    <div className="quality-check">
      <div className="quality-check-heading">
        <div><span className="eyebrow">{checkpointBadge(checkpoint)}</span><p>{d.instructions}</p></div>
        <span className={`status-pill ${completed?'status-complete':'status-open'}`}>{completed?'Complete':'Photo required'}</span>
      </div>
      {completed?<div className="quality-check-result">
        <strong>{check.damage_flagged?`Damage · ${label(check.damage_attribution)}`:'No damage found'}</strong>
        {check.damaged_product_id&&<span>{d.damage_parts.find(p=>p.product_id===check.damaged_product_id)?.name??'BOM part'} · Qty {check.damaged_quantity}</span>}
        {check.notes&&<span>{check.notes}</span>}
        <span>{check.photo_count} photo{check.photo_count===1?'':'s'} in Shared Attachments{check.completed_at?` · ${new Date(check.completed_at).toLocaleString()}`:''}</span>
        {check.maintenance_work_order_number&&<span><strong>Maintenance:</strong> {check.maintenance_work_order_number}</span>}
        {check.damage_claim_number&&<span><strong>Finance damage claim:</strong> {check.damage_claim_number}</span>}
      </div>:<>
        <div className="quality-photo-upload">
          <label className="phase-file-picker"><span>{file?file.name:'Choose QC picture'}</span><input type="file" accept="image/*" disabled={busy} onChange={e=>setFile(e.target.files?.[0]??null)}/></label>
          <button disabled={busy||!file} onClick={()=>void upload()}>Upload picture</button>
          <small>{check.photo_count} uploaded · Pictures also appear in Dispatch and Field Shared Attachments.</small>
        </div>
        <div className="quality-condition">
          <label><input type="radio" checked={!damaged} onChange={()=>setDamaged(false)}/> No damage</label>
          <label><input type="radio" checked={damaged} onChange={()=>setDamaged(true)}/> Flag damage</label>
        </div>
        {damaged&&<div className="quality-damage-grid">
          <label>Damaged BOM part<select value={productId} onChange={e=>setProductId(e.target.value)}><option value="">Select part</option>{d.damage_parts.map(p=><option value={p.product_id} key={p.product_id}>{p.name}{p.sku?` · ${p.sku}`:''} · {money(Number(p.unit_cost||0))}</option>)}</select></label>
          <label>Quantity<input type="number" min="0.01" step="0.01" value={quantity} onChange={e=>setQuantity(Number(e.target.value))}/></label>
          <div className="quality-cost"><span>Recorded part cost</span><strong>{selected?money(Number(selected.unit_cost||0)):'—'}</strong>{selected&&Number(selected.unit_cost||0)<=0&&<small>Add an acquisition or product cost before a billable claim can be created.</small>}</div>
        </div>}
        <label className="quality-notes">QC notes<textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder={damaged?'Describe the damage and where it was found.':'Optional condition notes'}/></label>
        <button className="primary" disabled={busy||check.photo_count<1||(damaged&&(!productId||quantity<=0||!notes.trim()))} onClick={()=>void complete()}>
          {damaged?'Flag Damage & Complete QC':'Mark Clear & Complete QC'}
        </button>
      </>}
      {error&&<div className="notice notice-danger">{error}</div>}
      {notice&&<div className="notice notice-success">{notice}</div>}
    </div>
  </Card>;
}
